/**
 * Hand-written, tiny REST client for the POLST public API.
 *
 * Goals (per POL-581):
 *   - Zero runtime dependencies (lives inside the 30 KB gzipped widget budget).
 *   - Configurable `baseUrl` (default https://api.polst.app).
 *   - Every request carries a persistent `X-Device-Id` header, generated
 *     once with `crypto.randomUUID()` and stored in
 *     `localStorage["polst.deviceId"]`.
 *   - Vote requests auto-attach an `Idempotency-Key` header + body key so
 *     retries can be safely deduped by the server.
 *   - Retry policy: up to 3 retries on 5xx with exponential backoff;
 *     0 retries on 4xx.
 *   - Error surface: always `PolstApiError(code, message, status)`.
 *
 * Collaborators (fetch, clock, uuid, storage, sleep) are all injectable so
 * tests can drive the client deterministically.
 */
import { DEFAULT_API_ORIGIN, getApiOrigin } from "./config";
import { PolstApiError } from "./errors";

/**
 * Default REST base URL baked in at build time (via esbuild `define`).
 * Runtime overrides from `configureOrigins()` win on every new client
 * — read `getApiOrigin()` at construction rather than this constant if
 * you need the live value. Kept exported for back-compat with callers
 * that assert on the build-time default.
 */
export const DEFAULT_BASE_URL = DEFAULT_API_ORIGIN;

/** localStorage key used to persist the per-browser device id. */
export const DEVICE_ID_STORAGE_KEY = "polst.deviceId";

export interface RetryPolicy {
  /** Max retries after the first attempt. Default: 3. */
  retries: number;
  /** Initial backoff in milliseconds. Default: 100. */
  baseDelayMs: number;
}

export const DEFAULT_RETRY_POLICY: RetryPolicy = {
  retries: 3,
  baseDelayMs: 100,
};

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface RestClientOptions {
  /** REST base URL (default: `https://api.polst.app`). */
  baseUrl?: string;
  /** Override for `globalThis.fetch`. */
  fetch?: typeof fetch;
  /** Override for `crypto.randomUUID`. */
  randomUUID?: () => string;
  /** Override for `localStorage`. Set `null` to disable persistence. */
  storage?: StorageLike | null;
  /** Retry policy. Merged over defaults. */
  retry?: Partial<RetryPolicy>;
  /** Sleep helper (testing). */
  sleep?: (ms: number) => Promise<void>;
}

export interface RequestOptions {
  /** REST base URL override for a single call. */
  baseUrl?: string;
  /** Abort signal forwarded to `fetch`. */
  signal?: AbortSignal;
}

interface FetchOptions extends RequestOptions {
  method: "GET" | "POST";
  path: string;
  body?: unknown;
  /** Extra headers merged into the request. */
  headers?: Record<string, string>;
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * RFC 4122 v4 UUID fallback when `crypto.randomUUID` is unavailable
 * (older Safari, iframes without secure context, etc.). Uses
 * `crypto.getRandomValues` when available, and a `Math.random` fallback
 * as last resort — the fallback is only used to seed device ids, so
 * cryptographic strength is not a correctness requirement.
 */
export function uuidv4Fallback(): string {
  const bytes = new Uint8Array(16);
  const g: { crypto?: { getRandomValues?: (a: Uint8Array) => Uint8Array } } =
    globalThis as unknown as {
      crypto?: { getRandomValues?: (a: Uint8Array) => Uint8Array };
    };
  if (g.crypto && typeof g.crypto.getRandomValues === "function") {
    g.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  // Per RFC 4122: set version (4) and variant (10xx) bits.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex: string[] = [];
  for (let i = 0; i < 16; i++) hex.push((bytes[i] ?? 0).toString(16).padStart(2, "0"));
  return (
    hex.slice(0, 4).join("") +
    "-" +
    hex.slice(4, 6).join("") +
    "-" +
    hex.slice(6, 8).join("") +
    "-" +
    hex.slice(8, 10).join("") +
    "-" +
    hex.slice(10, 16).join("")
  );
}

function resolveStorage(explicit: StorageLike | null | undefined): StorageLike | null {
  if (explicit === null) return null;
  if (explicit) return explicit;
  try {
    const ls = (globalThis as unknown as { localStorage?: StorageLike }).localStorage;
    if (
      ls &&
      typeof ls.getItem === "function" &&
      typeof ls.setItem === "function"
    ) {
      // Probe access — throws in some sandboxed contexts.
      ls.getItem(DEVICE_ID_STORAGE_KEY);
      return ls;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function resolveRandomUUID(explicit: (() => string) | undefined): () => string {
  if (explicit) return explicit;
  const g: { crypto?: { randomUUID?: () => string } } = globalThis as unknown as {
    crypto?: { randomUUID?: () => string };
  };
  if (g.crypto && typeof g.crypto.randomUUID === "function") {
    const native = g.crypto.randomUUID.bind(g.crypto);
    return () => native();
  }
  return uuidv4Fallback;
}

function resolveFetch(explicit: typeof fetch | undefined): typeof fetch {
  if (explicit) return explicit;
  const g = globalThis as unknown as { fetch?: typeof fetch };
  if (!g.fetch) {
    throw new PolstApiError(
      "no_fetch",
      "Global fetch is unavailable; pass options.fetch explicitly.",
      0,
    );
  }
  return g.fetch.bind(globalThis);
}

function joinUrl(baseUrl: string, path: string): string {
  const trimmedBase = baseUrl.replace(/\/+$/, "");
  const trimmedPath = path.startsWith("/") ? path : `/${path}`;
  return `${trimmedBase}${trimmedPath}`;
}

function shouldRetry(status: number): boolean {
  return status >= 500 && status < 600;
}

export class RestClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly randomUUID: () => string;
  private readonly storage: StorageLike | null;
  private readonly retry: RetryPolicy;
  private readonly sleep: (ms: number) => Promise<void>;
  private deviceIdCache: string | null = null;

  constructor(options: RestClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? getApiOrigin();
    this.fetchImpl = resolveFetch(options.fetch);
    this.randomUUID = resolveRandomUUID(options.randomUUID);
    this.storage = resolveStorage(options.storage);
    this.retry = { ...DEFAULT_RETRY_POLICY, ...(options.retry ?? {}) };
    this.sleep = options.sleep ?? defaultSleep;
  }

  /**
   * Return the persistent device id, generating + storing it if this is
   * the first time we're asked on this browser.
   */
  public getDeviceId(): string {
    if (this.deviceIdCache) return this.deviceIdCache;
    if (this.storage) {
      try {
        const existing = this.storage.getItem(DEVICE_ID_STORAGE_KEY);
        if (existing && typeof existing === "string" && existing.length > 0) {
          this.deviceIdCache = existing;
          return existing;
        }
      } catch {
        /* read errors fall through to generation */
      }
    }
    const fresh = this.randomUUID();
    this.deviceIdCache = fresh;
    if (this.storage) {
      try {
        this.storage.setItem(DEVICE_ID_STORAGE_KEY, fresh);
      } catch {
        /* write failures (quota, disabled) are non-fatal */
      }
    }
    return fresh;
  }

  /** Generate an idempotency key for a single mutation. */
  public newIdempotencyKey(): string {
    return this.randomUUID();
  }

  public get<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>({ method: "GET", path, ...options });
  }

  public post<T>(
    path: string,
    body: unknown,
    options: RequestOptions = {},
  ): Promise<T> {
    return this.request<T>({ method: "POST", path, body, ...options });
  }

  /**
   * Voting is a POST + idempotency. Returns the parsed JSON body.
   */
  public vote<T>(
    path: string,
    body: Record<string, unknown>,
    options: RequestOptions = {},
  ): Promise<T> {
    const idempotencyKey = this.newIdempotencyKey();
    return this.request<T>({
      method: "POST",
      path,
      body: { ...body, idempotencyKey },
      headers: { "Idempotency-Key": idempotencyKey },
      ...options,
    });
  }

  private async request<T>(opts: FetchOptions): Promise<T> {
    const base = opts.baseUrl ?? this.baseUrl;
    const url = joinUrl(base, opts.path);
    const headers: Record<string, string> = {
      Accept: "application/json",
      "X-Device-Id": this.getDeviceId(),
      ...(opts.headers ?? {}),
    };
    const init: RequestInit = {
      method: opts.method,
      headers,
      signal: opts.signal,
    };
    if (opts.body !== undefined) {
      headers["Content-Type"] = "application/json";
      init.body = JSON.stringify(opts.body);
    }

    let lastError: PolstApiError | null = null;
    const maxAttempts = this.retry.retries + 1;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      let response: Response;
      try {
        response = await this.fetchImpl(url, init);
      } catch (err) {
        // Network failure / abort / CORS preflight failure. Never retried —
        // we cannot distinguish a transient outage from a misconfiguration,
        // and retrying blind requests risks duplicate votes without the
        // server-side idempotency guard kicking in (the vote endpoint has
        // it; others do not).
        if ((err as { name?: string }).name === "AbortError") {
          throw new PolstApiError("aborted", "Request was aborted.", 0);
        }
        throw new PolstApiError(
          "network_error",
          (err as Error).message || "Network request failed.",
          0,
        );
      }

      if (response.ok) {
        return (await parseJsonBody<T>(response)) as T;
      }

      const err = await buildErrorFromResponse(response);
      if (!shouldRetry(response.status) || attempt === maxAttempts - 1) {
        throw err;
      }
      lastError = err;
      const delay = this.retry.baseDelayMs * 2 ** attempt;
      await this.sleep(delay);
    }
    // Unreachable: the loop always either returns or throws on the last
    // attempt. TS doesn't know that, so give it a sensible error.
    throw (
      lastError ??
      new PolstApiError("unknown", "Request failed for unknown reasons.", 0)
    );
  }
}

async function parseJsonBody<T>(response: Response): Promise<T | undefined> {
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined;
  }
}

interface ErrorBody {
  code?: string;
  error?: string;
  message?: string;
}

async function buildErrorFromResponse(response: Response): Promise<PolstApiError> {
  const body = (await parseJsonBody<ErrorBody>(response)) ?? {};
  const code = body.code ?? body.error ?? `http_${response.status}`;
  const message =
    body.message ?? response.statusText ?? `Request failed with status ${response.status}`;
  return new PolstApiError(code, message, response.status);
}
